import React, { useLayoutEffect, useMemo, useRef } from "react";
import { mapCategoryName, TransactionDirectionType } from "@common/categories";
import { Category } from "@common/types/category";
import { CategoryIcon } from "@components/transactions/CategoryIcon";
import { CategoryCombobox } from "@components/transactions/TransactionSplitV2/CategoryCombobox";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useCreationModalPending } from "./useCreationModalPending";
import styles from "./CategoryCell.module.scss";
import { BaseCellProps } from "./index";

export const CategoryCell: React.FC<BaseCellProps<string>> = ({
    value,
    isEditing,
    finishEditing,
    rowIdx,
    colId,
    enterEditModeKey,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { isPendingRef, setCreationModalPending } = useCreationModalPending();
    const triggerClassName = useMemo(
        () => `category-cell-trigger-${rowIdx}-${colId}`,
        [rowIdx, colId],
    );
    const categoriesById = useCategoriesById();
    const selectedCategory = value ? categoriesById[value] : undefined;

    useLayoutEffect(() => {
        if (isEditing && containerRef.current) {
            const trigger = containerRef.current.querySelector(
                `.${triggerClassName}`,
            ) as HTMLElement;
            trigger?.click();
        }
    }, [isEditing, triggerClassName]);

    if (isEditing) {
        return (
            <>
                <div
                    ref={containerRef}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (["Enter", "Escape"].includes(e.key)) {
                            finishEditing();
                        }
                    }}
                >
                    <CategoryCombobox
                        selectedCategory={selectedCategory}
                        onChange={(category) =>
                            finishEditing(category?.id ?? null)
                        }
                        onOpenChange={(open) => {
                            if (!open && !isPendingRef.current) {
                                finishEditing();
                            }
                        }}
                        onCreationModalOpen={() => {
                            setCreationModalPending(true);
                        }}
                        onCreationModalClose={() => {
                            setCreationModalPending(false);
                            finishEditing();
                        }}
                        cellEditor={{ openingKey: enterEditModeKey }}
                        hideArrow
                        triggerButtonProps={{
                            variant: "primary",
                            size: "sm",
                            className: triggerClassName,
                            style: {
                                position: "absolute",
                                opacity: 0,
                                pointerEvents: "none",
                            },
                        }}
                    />
                </div>
                <CategoryValueDisplay category={selectedCategory} />
            </>
        );
    }

    return <CategoryValueDisplay category={selectedCategory} />;
};

const CategoryValueDisplay: React.FC<{ category?: Category }> = ({
    category,
}) => {
    if (!category) {
        return null;
    }

    return (
        <div className={styles.categoryCell}>
            <CategoryIcon categoryId={category.id} />
            <span className={styles.categoryLabel}>
                {mapCategoryName({
                    category,
                    transactionDirection: TransactionDirectionType.mixed,
                })}
            </span>
        </div>
    );
};
